import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, Users, Monitor, Shield, Plus, CheckCircle2,
  AlertCircle, ChevronRight, Fingerprint, Eye, Trash2, Key,
  Mail, Phone, MapPin, X, Check, Laptop, Radio, Sparkles, RefreshCw,
  BarChart3, TrendingUp, Calendar, Download, FileSpreadsheet, Search,
  Filter, Cpu, ArrowUpRight, Clock, Activity, Zap, Edit2
} from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';

export default function SaasClientsManagement() {
  const [clients, setClients] = useState([]);
  const [unassignedDevices, setUnassignedDevices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' | 'analytics' | 'fleet' | 'reports'

  // Search and Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('ALL');
  const [filterHardware, setFilterHardware] = useState('ALL');

  // Onboard modal state
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    organization_name: '',
    legal_name: '',
    contact_email: '',
    password: '',
    admin_first_name: '',
    admin_last_name: '',
    mobile_number: '',
    address: '',
    plan_tier: 'T700_STANDARD',
    staff_limit: 50,
    assigned_dev_ids: []
  });

  // Edit client modal
  const [editModal, setEditModal] = useState({
    open: false,
    org: null,
    form: {
      organization_name: '',
      legal_name: '',
      address: '',
      plan_tier: 'T700_STANDARD',
      staff_limit: 50,
      status: 'active'
    },
    saving: false
  });

  // Assign device modal
  const [assignModal, setAssignModal] = useState({
    open: false,
    org: null,
    selectedDevId: '',
    saving: false
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [clientsRes, devsRes, analyticsRes] = await Promise.all([
        axiosInstance.get('/api/organization/saas/clients'),
        axiosInstance.get('/api/organization/saas/unassigned-devices'),
        axiosInstance.get('/api/organization/saas/analytics').catch(() => ({ data: { status: false } }))
      ]);

      if (clientsRes.data?.status) {
        setClients(clientsRes.data.data || []);
      }
      if (devsRes.data?.status) {
        setUnassignedDevices(devsRes.data.data || []);
      }
      if (analyticsRes.data?.status) {
        setAnalytics(analyticsRes.data.data || null);
      }
    } catch (err) {
      console.error('Failed to load SaaS clients:', err);
      setError(err.response?.data?.message || 'Failed to load client companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalStaffEnrolled = useMemo(() => {
    return clients.reduce((acc, c) => acc + (Number(c.staff_count) || 0), 0);
  }, [clients]);

  const totalMachinesBound = useMemo(() => {
    return clients.reduce((acc, c) => acc + (c.devices?.length || 0), 0);
  }, [clients]);

  const assignedDevices = useMemo(() => {
    const list = [];
    clients.forEach((org) => {
      (org.devices || []).forEach((dev) => {
        list.push({
          ...dev,
          orgName: org.organization_name,
          orgLegalName: org.legal_name,
          orgId: org.organization_id_PK,
          orgPlan: org.plan_tier,
          orgLogo: org.organization_logo
        });
      });
    });
    return list;
  }, [clients]);

  // Filtered clients list
  const filteredClients = useMemo(() => {
    return clients.filter((org) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        org.organization_name?.toLowerCase().includes(q) ||
        org.legal_name?.toLowerCase().includes(q) ||
        org.contact_email?.toLowerCase().includes(q) ||
        String(org.organization_id_PK).includes(q);

      const matchesPlan =
        filterPlan === 'ALL' || org.plan_tier === filterPlan;

      const hasDevices = org.devices && org.devices.length > 0;
      const matchesHardware =
        filterHardware === 'ALL' ||
        (filterHardware === 'BOUND' && hasDevices) ||
        (filterHardware === 'UNBOUND' && !hasDevices);

      return matchesSearch && matchesPlan && matchesHardware;
    });
  }, [clients, searchQuery, filterPlan, filterHardware]);

  // Format Date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // CSV Export
  const exportClientsCSV = () => {
    if (!clients.length) return alert('No client data to export');
    const headers = [
      'Org ID',
      'Organization Name',
      'Legal Name',
      'Contact Email',
      'Address',
      'Plan Tier',
      'Staff Enrolled',
      'Staff Limit',
      'Capacity Used (%)',
      'Assigned Devices',
      'Onboarded Date'
    ];

    const rows = clients.map((c) => {
      const devList = (c.devices || []).map((d) => d.dev_id).join('; ');
      const capacity = Math.min(
        100,
        Math.round(((c.staff_count || 0) / (c.staff_limit || 50)) * 100)
      );
      return [
        c.organization_id_PK,
        `"${c.organization_name || ''}"`,
        `"${c.legal_name || ''}"`,
        `"${c.contact_email || ''}"`,
        `"${(c.address || '').replace(/"/g, '""')}"`,
        c.plan_tier || 'T700_STANDARD',
        c.staff_count || 0,
        c.staff_limit || 50,
        `${capacity}%`,
        `"${devList}"`,
        formatDate(c.created_at)
      ];
    });

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `AroGeek_SaaS_Clients_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    if (
      !onboardForm.organization_name ||
      !onboardForm.contact_email ||
      !onboardForm.password ||
      !onboardForm.admin_first_name
    ) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setOnboardSubmitting(true);
      const res = await axiosInstance.post('/api/organization/saas/onboard', onboardForm);
      if (res.data?.status) {
        setShowOnboardModal(false);
        setOnboardForm({
          organization_name: '',
          legal_name: '',
          contact_email: '',
          password: '',
          admin_first_name: '',
          admin_last_name: '',
          mobile_number: '',
          address: '',
          plan_tier: 'T700_STANDARD',
          staff_limit: 50,
          assigned_dev_ids: []
        });
        fetchData();
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Onboarding failed');
    } finally {
      setOnboardSubmitting(false);
    }
  };

  const handleAssignDeviceSubmit = async () => {
    if (!assignModal.selectedDevId || !assignModal.org) return;
    try {
      setAssignModal((prev) => ({ ...prev, saving: true }));
      await axiosInstance.post('/api/organization/saas/assign-device', {
        dev_id: assignModal.selectedDevId,
        organization_id_PK: assignModal.org.organization_id_PK
      });
      setAssignModal({ open: false, org: null, selectedDevId: '', saving: false });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign device');
      setAssignModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleUnassignDevice = async (devId) => {
    if (!window.confirm(`Unassign biometric machine ${devId} from this company?`)) return;
    try {
      await axiosInstance.post('/api/organization/saas/assign-device', {
        dev_id: devId,
        organization_id_PK: null
      });
      fetchData();
    } catch (err) {
      alert('Failed to unassign device');
    }
  };

  const handleOpenEditModal = (org) => {
    setEditModal({
      open: true,
      org,
      form: {
        organization_name: org.organization_name || '',
        legal_name: org.legal_name || org.organization_name || '',
        address: org.address || '',
        plan_tier: org.plan_tier || 'T700_STANDARD',
        staff_limit: org.staff_limit || 50,
        status: org.status || 'active'
      },
      saving: false
    });
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if (!editModal.org) return;
    try {
      setEditModal((prev) => ({ ...prev, saving: true }));
      const res = await axiosInstance.put(
        `/api/organization/saas/client/${editModal.org.organization_id_PK}`,
        editModal.form
      );
      if (res.data?.status) {
        setEditModal({ open: false, org: null, form: {}, saving: false });
        fetchData();
      } else {
        alert(res.data?.message || 'Failed to update client');
        setEditModal((prev) => ({ ...prev, saving: false }));
      }
    } catch (err) {
      console.error('Update client error:', err);
      alert(err.response?.data?.message || 'Error updating client');
      setEditModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteDevice = async (devId) => {
    if (!window.confirm(`Are you sure you want to delete biometric machine ${devId} from inventory stock?`)) return;
    try {
      const res = await axiosInstance.delete(`/api/organization/saas/device/${devId}`);
      if (res.data?.status) {
        fetchData();
      } else {
        alert(res.data?.message || 'Failed to delete device');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting device');
    }
  };

  const handleDeleteClient = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to permanently delete client organization "${orgName}" (ID: #${orgId})? All its employees, punches, and settings will be removed.`)) return;
    try {
      const res = await axiosInstance.delete(`/api/organization/saas/client/${orgId}`);
      if (res.data?.status) {
        fetchData();
      } else {
        alert(res.data?.message || 'Failed to delete client organization');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting organization');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-20 font-sans">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* SaaS Super Admin Hero Header */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-indigo-950 to-blue-950 text-white shadow-2xl mb-8 p-6 sm:p-8 border border-white/10">
          <div className="absolute top-0 right-0 -mt-12 -mr-12 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  SaaS Platform SuperAdmin
                </span>
                <span className="text-xs text-slate-400 hidden sm:inline">
                  PagarBook & Petpooja Biometric Standard
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
                AroGeek Multi-Tenant Cloud Platform
              </h1>
              <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
                Central control for client organizations, T700 / T800 biometric hardware licensing, live attendance streams, and subscription quotas.
              </p>
            </div>

            {/* Quick Action Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={exportClientsCSV}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold border border-white/15 backdrop-blur-md transition-all shadow-sm active:scale-95"
                title="Download CSV report of all clients"
              >
                <Download className="w-4 h-4 text-blue-300" />
                Export SaaS Report
              </button>
              <button
                onClick={() => setShowOnboardModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-bold shadow-lg shadow-blue-500/30 transition-all active:scale-95"
              >
                <Plus className="w-4 h-4" />
                Onboard Company
              </button>
              <button
                onClick={fetchData}
                className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition active:scale-95"
                title="Refresh Platform Data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </section>

        {/* Top KPI Cards (PagarBook / Petpooja Commercial Standard) */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Card 1: Active Companies */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Client Companies
              </span>
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                <Building2 className="w-5 h-5" />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-slate-900">{clients.length}</span>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                Active Tenants
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {clients.filter((c) => c.plan_tier === 'T800_ENTERPRISE').length} Enterprise •{' '}
              {clients.filter((c) => c.plan_tier !== 'T800_ENTERPRISE').length} Standard
            </p>
          </div>

          {/* Card 2: Total Enrolled Workforce */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Enrolled Workforce
              </span>
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-indigo-600">{totalStaffEnrolled}</span>
              <span className="text-xs font-semibold text-slate-500">Staff Members</span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Across all client companies
            </p>
          </div>

          {/* Card 3: Today's Live Punches */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Today's Punches
              </span>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                <Activity className="w-5 h-5" />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-amber-600">
                {analytics?.stats?.today_punches || 0}
              </span>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Cloud biometric stream
            </p>
          </div>

          {/* Card 4: Hardware Machine Fleet */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Hardware Fleet
              </span>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                <Monitor className="w-5 h-5" />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-emerald-600">
                {totalMachinesBound}
              </span>
              <span className="text-xs font-semibold text-slate-500">
                / {totalMachinesBound + unassignedDevices.length} Total
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              <span className="text-amber-600 font-semibold">{unassignedDevices.length}</span> in stock ready to assign
            </p>
          </div>
        </section>

        {/* Navigation Tabs (Client List vs Analytics Graphs vs Hardware Fleet vs Reports) */}
        <div className="flex items-center gap-2 border-b border-slate-200 mb-6 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
              activeTab === 'clients'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Client Organizations ({clients.length})
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
              activeTab === 'analytics'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Visual Graphs & Analytics
          </button>

          <button
            onClick={() => setActiveTab('fleet')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
              activeTab === 'fleet'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Cpu className="w-4 h-4" />
            Hardware Fleet ({totalMachinesBound + unassignedDevices.length})
          </button>

          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
              activeTab === 'reports'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            SaaS Reports & Audit
          </button>
        </div>

        {/* TAB 1: CLIENT ORGANIZATIONS */}
        {activeTab === 'clients' && (
          <div>
            {/* Search and Filters Bar */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-sm mb-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by company name, contact email, or Org ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Filter className="w-3.5 h-3.5" />
                  Plan:
                </div>
                <select
                  value={filterPlan}
                  onChange={(e) => setFilterPlan(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ALL">All Plans</option>
                  <option value="T700_STANDARD">T700 Standard (RFID/Finger)</option>
                  <option value="T800_ENTERPRISE">T800 Enterprise (Face/Palm)</option>
                </select>

                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  Hardware:
                </div>
                <select
                  value={filterHardware}
                  onChange={(e) => setFilterHardware(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ALL">All Clients</option>
                  <option value="BOUND">Machine Assigned</option>
                  <option value="UNBOUND">No Machine</option>
                </select>
              </div>
            </div>

            {/* Organizations Grid */}
            {loading && clients.length === 0 ? (
              <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Loading client organizations...</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="bg-white rounded-2xl p-16 text-center border border-slate-200">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-slate-800 mb-1">No Matching Companies</h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto mb-4">
                  No client organizations found matching your search or filter parameters.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterPlan('ALL');
                    setFilterHardware('ALL');
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {filteredClients.map((org) => {
                  const isT800 = org.plan_tier === 'T800_ENTERPRISE';
                  const staffUsagePercent = Math.min(
                    100,
                    Math.round(((org.staff_count || 0) / (org.staff_limit || 50)) * 100)
                  );
                  const hasDevices = org.devices && org.devices.length > 0;

                  return (
                    <article
                      key={org.organization_id_PK}
                      className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition p-6"
                    >
                      {/* Top Row: Logo, Title, Badges, Onboarded Date, Staff Limit */}
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-100">
                        {/* Company Identity */}
                        <div className="flex items-start sm:items-center gap-4">
                          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-slate-200 flex items-center justify-center font-bold text-xl text-blue-700 overflow-hidden flex-shrink-0">
                            {org.organization_logo ? (
                              <img
                                src={org.organization_logo}
                                alt=""
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              org.organization_name ? org.organization_name[0] : 'C'
                            )}
                          </div>

                          <div>
                            <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                              <h3 className="text-xl font-bold text-slate-900">
                                {org.organization_name}
                              </h3>
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                                  isT800
                                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                    : 'bg-blue-100 text-blue-800 border border-blue-200'
                                }`}
                              >
                                {isT800 ? 'T800 Enterprise (Face/Palm)' : 'T700 Standard (RFID/Finger)'}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Active
                              </span>
                              <button
                                onClick={() => handleOpenEditModal(org)}
                                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition ml-1"
                                title="Edit Company Details & Hardware Plan"
                              >
                                <Edit2 className="w-3 h-3 text-slate-600" />
                                Edit Plan
                              </button>
                            </div>

                            {/* Meta items: Email, Location, Onboarded Date, Org ID */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 font-medium">
                              <span className="flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-slate-400" />
                                {org.contact_email}
                              </span>
                              {org.address && (
                                <span className="flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                                  {org.address}
                                </span>
                              )}
                              {/* Prominent Onboarding Date */}
                              <span className="flex items-center gap-1.5 font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
                                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                                Onboarded: {formatDate(org.created_at)}
                              </span>
                              <span className="text-slate-400">
                                Org ID: #{org.organization_id_PK}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Staff Limit Meter */}
                        <div className="min-w-[220px]">
                          <div className="flex items-center justify-between text-xs font-semibold mb-1">
                            <span className="text-slate-500">Staff Limit Quota</span>
                            <span className="text-slate-900 font-bold">
                              {org.staff_count || 0} / {org.staff_limit || 50} Staff ({staffUsagePercent}%)
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                staffUsagePercent > 85
                                  ? 'bg-amber-500'
                                  : staffUsagePercent > 50
                                  ? 'bg-blue-600'
                                  : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.max(5, staffUsagePercent)}%` }}
                            />
                          </div>
                          <p className="text-[11px] text-slate-400 mt-1 text-right">
                            {(org.staff_limit || 50) - (org.staff_count || 0)} slots available
                          </p>
                        </div>
                      </div>

                      {/* Bottom Section: Assigned Biometric Hardware */}
                      <div className="pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3.5 h-3.5 text-blue-500" />
                            Assigned Biometric Machine Hardware ({org.devices?.length || 0})
                          </p>

                          {hasDevices ? (
                            <div className="flex flex-wrap gap-2.5">
                              {org.devices.map((dev) => (
                                <div
                                  key={dev.dev_id}
                                  className="inline-flex items-center gap-3 bg-slate-50 hover:bg-slate-100/80 border border-slate-200/80 rounded-xl px-3.5 py-2 text-xs transition"
                                >
                                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Hardware Active" />
                                  <div>
                                    <span className="font-mono font-bold text-slate-800">
                                      {dev.dev_id}
                                    </span>
                                    <span className="text-[11px] text-slate-500 ml-2">
                                      ({dev.name || (isT800 ? 'T800 Face/Palm' : 'T700 Biometric')})
                                    </span>
                                    {dev.ip && (
                                      <span className="text-[11px] text-slate-400 ml-2 font-mono">
                                        IP: {dev.ip}
                                      </span>
                                    )}
                                  </div>

                                  <button
                                    onClick={() => handleUnassignDevice(dev.dev_id)}
                                    className="p-1 text-slate-400 hover:text-red-600 rounded-lg transition"
                                    title="Unassign machine from this company"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-amber-600 font-medium flex items-center gap-1.5">
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                              No biometric machine bound yet. Punch attendance will be inactive until hardware is assigned.
                            </p>
                          )}
                        </div>

                        {/* Machine Action Buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenEditModal(org)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 font-semibold text-xs text-slate-700 transition border border-slate-200"
                            title="Edit Plan Tier, Staff Quota & Company Info"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-slate-600" />
                            Edit Details / Plan
                          </button>
                          <button
                            onClick={() =>
                              setAssignModal({
                                open: true,
                                org,
                                selectedDevId: unassignedDevices[0]?.dev_id || '',
                                saving: false
                              })
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 font-semibold text-xs text-blue-700 transition border border-blue-200/60"
                          >
                            <Plus className="w-3.5 h-3.5 text-blue-600" />
                            {hasDevices ? 'Change / Add Machine' : 'Bind Hardware Machine'}
                          </button>
                          <button
                            onClick={() => handleDeleteClient(org.organization_id_PK, org.organization_name)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 hover:bg-red-100 font-semibold text-xs text-red-700 transition border border-red-200"
                            title="Delete Client Organization"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: VISUAL GRAPHS & ANALYTICS */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            {/* 7-Day Live Punch Trend Chart */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/90 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                    <h3 className="text-lg font-bold text-slate-900">
                      7-Day Platform Biometric Punch Activity
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500">
                    Live aggregate punch logs captured from T700 / T800 biometric devices across all client organizations.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black text-blue-600">
                    {analytics?.punchTrends?.reduce((a, b) => a + (Number(b.count) || 0), 0) || 0}
                  </span>
                  <span className="text-xs text-slate-400 block">Total Punches This Week</span>
                </div>
              </div>

              {/* Bar Chart Visualization */}
              {analytics?.punchTrends && analytics.punchTrends.length > 0 ? (
                <div className="pt-6 pb-2">
                  <div className="h-48 flex items-end gap-3 sm:gap-6 border-b border-slate-200 px-4">
                    {(() => {
                      const maxCount = Math.max(
                        ...analytics.punchTrends.map((t) => Number(t.count) || 1),
                        10
                      );
                      return analytics.punchTrends.map((trend, idx) => {
                        const count = Number(trend.count) || 0;
                        const heightPercent = Math.max(12, Math.round((count / maxCount) * 100));
                        return (
                          <div
                            key={trend.date || idx}
                            className="flex-1 flex flex-col items-center gap-2 group relative"
                          >
                            {/* Hover Tooltip */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 bg-slate-900 text-white text-[11px] font-bold py-1 px-2 rounded-md shadow pointer-events-none whitespace-nowrap">
                              {count} Punches
                            </div>
                            <div
                              className="w-full max-w-[48px] rounded-t-xl bg-gradient-to-t from-blue-600 to-indigo-500 group-hover:from-blue-500 group-hover:to-indigo-400 transition-all shadow-sm"
                              style={{ height: `${heightPercent}%` }}
                            />
                            <span className="text-[11px] font-semibold text-slate-500">
                              {formatDate(trend.date).split(' ')[0]} {formatDate(trend.date).split(' ')[1]}
                            </span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ) : (
                <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                  No punch records in the last 7 days yet. Punches will appear dynamically once machines punch.
                </div>
              )}
            </div>

            {/* Two-Column Breakdown: Plan Distribution & Recent Punches */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Card 1: Subscription Tier Breakdown */}
              <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-sm">
                <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  Subscription Plan Breakdown
                </h3>

                <div className="space-y-4">
                  {/* T700 Standard */}
                  <div>
                    <div className="flex items-center justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-700">T700 Standard (RFID / Fingerprint)</span>
                      <span className="text-blue-600 font-bold">
                        {clients.filter((c) => c.plan_tier !== 'T800_ENTERPRISE').length} Clients
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-600 h-full rounded-full"
                        style={{
                          width: `${
                            clients.length
                              ? Math.round(
                                  (clients.filter((c) => c.plan_tier !== 'T800_ENTERPRISE').length /
                                    clients.length) *
                                    100
                                )
                              : 0
                          }%`
                        }}
                      />
                    </div>
                  </div>

                  {/* T800 Enterprise */}
                  <div>
                    <div className="flex items-center justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-700">T800 Enterprise (Face / Palm / Finger)</span>
                      <span className="text-purple-600 font-bold">
                        {clients.filter((c) => c.plan_tier === 'T800_ENTERPRISE').length} Clients
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                      <div
                        className="bg-purple-600 h-full rounded-full"
                        style={{
                          width: `${
                            clients.length
                              ? Math.round(
                                  (clients.filter((c) => c.plan_tier === 'T800_ENTERPRISE').length /
                                    clients.length) *
                                    100
                                )
                              : 0
                          }%`
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                  <span>Hardware Protocol: HTTP Port 4002</span>
                  <span className="text-emerald-600 font-bold">● Active & Listening</span>
                </div>
              </div>

              {/* Card 2: Recent Live Punches Across Platform */}
              <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-sm">
                <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-600" />
                  Recent Live Biometric Punches
                </h3>

                {analytics?.recentPunches && analytics.recentPunches.length > 0 ? (
                  <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
                    {analytics.recentPunches.map((punch) => (
                      <div
                        key={punch.id}
                        className="flex items-center justify-between p-2.5 bg-slate-50 hover:bg-slate-100 rounded-xl text-xs transition"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-xs">
                            {punch.verify_mode || 'FP'}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-800">
                              {punch.first_name ? `${punch.first_name} ${punch.last_name || ''}` : `Staff #${punch.device_user_id}`}
                            </span>
                            <span className="text-[11px] text-slate-400 block">
                              {punch.organization_name || 'Organization'} • Dev: {punch.dev_id?.slice(-8)}
                            </span>
                          </div>
                        </div>

                        <div className="text-right text-[11px] font-mono text-slate-500">
                          {punch.io_time ? new Date(punch.io_time).toLocaleTimeString() : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center text-slate-400 text-xs text-center">
                    No recent punches logged yet today.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: HARDWARE FLEET CONSOLE */}
        {activeTab === 'fleet' && (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Biometric Machine Fleet Inventory (T700 & T800)
                </h3>
                <p className="text-xs text-slate-500">
                  Manage physical biometric hardware provisioned to client organizations or available in stock.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href="/biometric-settings"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md transition"
                >
                  <Cpu className="w-4 h-4" />
                  Machine Cloud Settings
                </a>

                <a
                  href="/t700.html"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs border border-slate-200 transition"
                >
                  <Monitor className="w-4 h-4" />
                  Live Console
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>

            {/* FLEET KPI SUMMARY BAR */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Fleet Size</p>
                  <p className="text-xl font-bold text-slate-900 mt-0.5">{assignedDevices.length + unassignedDevices.length}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                  <Monitor className="w-5 h-5" />
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-emerald-50/60 border border-emerald-200/80 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Active in Tenants</p>
                  <p className="text-xl font-bold text-emerald-950 mt-0.5">{assignedDevices.length}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <Cpu className="w-5 h-5" />
                </div>
              </div>

              <div className="p-3.5 rounded-2xl bg-amber-50/60 border border-amber-200/80 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Stock Ready to Deploy</p>
                  <p className="text-xl font-bold text-amber-950 mt-0.5">{unassignedDevices.length}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <Laptop className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* 1. ACTIVE ASSIGNED HARDWARE FLEET */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-emerald-600" />
                  Assigned Hardware in Production ({assignedDevices.length})
                </h4>
                <span className="text-xs text-slate-400 font-medium">
                  {assignedDevices.length} machine{assignedDevices.length === 1 ? '' : 's'} actively bound to tenants
                </span>
              </div>

              {assignedDevices.length === 0 ? (
                <div className="bg-slate-50 rounded-2xl p-6 text-center text-xs text-slate-500 border border-slate-200/80">
                  No biometric machines are currently assigned to any client companies.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {assignedDevices.map((dev) => {
                    const isT800 =
                      dev.name?.includes('800') ||
                      dev.dev_id?.toString().startsWith('8');
                    return (
                      <div
                        key={dev.dev_id}
                        className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-md transition-all flex flex-col justify-between gap-4"
                      >
                        <div>
                          {/* Machine Header */}
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-slate-900 text-sm bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                                {dev.dev_id}
                              </span>
                              <span
                                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  isT800
                                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                    : 'bg-blue-100 text-blue-700 border border-blue-200'
                                }`}
                              >
                                {isT800 ? 'T800 Face/Palm' : 'T700 Biometric'}
                              </span>
                            </div>

                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                              Cloud Live
                            </span>
                          </div>

                          {/* Client Company Box */}
                          <div className="p-3.5 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50/40 border border-slate-200/80 mb-3">
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5 text-blue-600" />
                                Assigned Organization
                              </span>
                              <span className="font-mono text-slate-600 font-semibold bg-white px-1.5 py-0.5 rounded border border-slate-200">
                                Org #{dev.orgId}
                              </span>
                            </div>
                            <h5 className="font-bold text-slate-900 text-sm truncate">
                              {dev.orgName}
                            </h5>
                            {dev.orgLegalName && dev.orgLegalName !== dev.orgName && (
                              <p className="text-[11px] text-slate-500 truncate mt-0.5">
                                {dev.orgLegalName}
                              </p>
                            )}
                          </div>

                          {/* Hardware Specs Grid */}
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                              <span className="text-[10px] text-slate-400 font-medium block">
                                IP Address
                              </span>
                              <span className="font-mono font-semibold text-slate-800 text-xs">
                                {dev.ip || '168.144.28.127'}
                              </span>
                            </div>
                            <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                              <span className="text-[10px] text-slate-400 font-medium block">
                                Firmware
                              </span>
                              <span className="font-semibold text-slate-800 text-xs">
                                v{dev.firmware || '1.0'}
                              </span>
                            </div>
                          </div>

                          {dev.last_seen && (
                            <p className="text-[11px] text-slate-400 font-mono mt-2.5 flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              Last Seen: {formatDate(dev.last_seen)}
                            </p>
                          )}
                        </div>

                        {/* Actions Footer */}
                        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                          <a
                            href={`/biometric-settings?dev_id=${dev.dev_id}`}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs transition border border-blue-200"
                          >
                            <Cpu className="w-3.5 h-3.5 text-blue-600" />
                            Machine Settings
                          </a>

                          <a
                            href={`/t700.html?dev_id=${dev.dev_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 font-bold text-xs text-slate-700 transition"
                            title="Open Hardware Logs Console"
                          >
                            <Monitor className="w-3.5 h-3.5 text-slate-600" />
                            Logs
                          </a>
                          <button
                            onClick={() => handleUnassignDevice(dev.dev_id)}
                            className="px-3.5 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 font-bold text-xs transition"
                            title="Unassign this machine back to Stock"
                          >
                            Unassign
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 2. UNASSIGNED STOCK IN INVENTORY */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                <Laptop className="w-4 h-4 text-amber-500" />
                Unassigned Machines in Stock ({unassignedDevices.length})
              </h4>

              {unassignedDevices.length === 0 ? (
                <div className="bg-slate-50 rounded-2xl p-6 text-center text-xs text-slate-500 border border-slate-200/80">
                  All biometric machines are currently assigned to client companies. Connect a new T700/T800 to port 4002 to auto-enroll.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {unassignedDevices.map((dev) => (
                    <div
                      key={dev.dev_id}
                      className="p-4 rounded-2xl border border-amber-200 bg-amber-50/40 flex flex-col justify-between gap-3"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-mono font-bold text-slate-900 text-sm">
                            {dev.dev_id}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
                            Ready to Assign
                          </span>
                        </div>
                        <p className="text-xs text-slate-600">
                          {dev.name || 'T700 Standard Biometric'} • FW: {dev.firmware || '1.0'}
                        </p>
                        {dev.ip && (
                          <p className="text-[11px] text-slate-500 font-mono">IP: {dev.ip}</p>
                        )}
                      </div>

                      {/* Unassigned Machine Actions */}
                      <div className="flex items-center gap-2 pt-2 border-t border-amber-200/60">
                        <a
                          href={`/biometric-settings?dev_id=${dev.dev_id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-xl bg-white hover:bg-amber-100/70 text-amber-900 font-bold text-xs transition border border-amber-300 shadow-2xs"
                          title="Configure Machine Settings (IP, Port, Time, Reboot)"
                        >
                          <Cpu className="w-3.5 h-3.5 text-amber-600" />
                          Machine Settings
                        </a>

                        <a
                          href={`/t700.html?dev_id=${dev.dev_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 py-1.5 px-2.5 rounded-xl bg-white hover:bg-amber-100/70 font-bold text-xs text-slate-700 transition border border-amber-300 shadow-2xs"
                          title="Open Hardware Logs Console"
                        >
                          <Monitor className="w-3.5 h-3.5 text-slate-600" />
                          Logs
                        </a>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setAssignModal({
                              open: true,
                              org: clients[0] || null,
                              selectedDevId: dev.dev_id,
                              saving: false
                            })
                          }
                          className="flex-1 text-center py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs transition shadow-sm"
                        >
                          Assign to Company
                        </button>
                        <button
                          onClick={() => handleDeleteDevice(dev.dev_id)}
                          className="p-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition"
                          title="Delete Machine from Inventory"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: SAAS AUDIT & REPORTS */}
        {activeTab === 'reports' && (
          <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              SaaS Reports & Client Licensing Audit
            </h3>
            <p className="text-xs text-slate-500 mb-6">
              Instant 1-click reporting downloads compatible with Excel, Google Sheets, and Petpooja/PagarBook data formats.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl border border-slate-200 bg-slate-50/50 flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">
                    Complete Client Organizations Directory
                  </h4>
                  <p className="text-xs text-slate-500 mb-4">
                    Full database export of all client organizations with legal registration, primary contacts, plan tier limits, active staff counts, and bound biometric serial numbers.
                  </p>
                </div>
                <button
                  onClick={exportClientsCSV}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-sm transition"
                >
                  <Download className="w-4 h-4" />
                  Download Directory CSV
                </button>
              </div>

              <div className="p-6 rounded-2xl border border-slate-200 bg-slate-50/50 flex flex-col justify-between">
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
                    <Zap className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">
                    Biometric Hardware Licensing Audit
                  </h4>
                  <p className="text-xs text-slate-500 mb-4">
                    Hardware inventory audit covering serial numbers, firmware versions, heartbeat status, and assigned company mappings.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const rows = [
                      ['Device Serial', 'Model', 'IP Address', 'Firmware', 'Assigned Company', 'Status']
                    ];
                    clients.forEach((c) => {
                      (c.devices || []).forEach((d) => {
                        rows.push([d.dev_id, d.name || 'T700', d.ip || '', d.firmware || '', c.organization_name, 'Bound']);
                      });
                    });
                    unassignedDevices.forEach((d) => {
                      rows.push([d.dev_id, d.name || 'T700', d.ip || '', d.firmware || '', 'None (In Stock)', 'Unassigned']);
                    });
                    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
                    const link = document.createElement('a');
                    link.href = encodeURI(csvContent);
                    link.download = `AroGeek_Hardware_Audit_${new Date().toISOString().split('T')[0]}.csv`;
                    link.click();
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-sm transition"
                >
                  <Download className="w-4 h-4" />
                  Download Hardware Audit CSV
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Onboard Company Modal */}
      {showOnboardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-slate-100 my-8">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  Onboard New Client Company
                </h3>
                <p className="text-xs text-slate-500">
                  Register tenant organization, configure staff limit, and assign hardware.
                </p>
              </div>
              <button
                onClick={() => setShowOnboardModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleOnboardSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={onboardForm.organization_name}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, organization_name: e.target.value })
                    }
                    placeholder="e.g. Acme Tech Pvt Ltd"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Legal / Registered Name
                  </label>
                  <input
                    type="text"
                    value={onboardForm.legal_name}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, legal_name: e.target.value })
                    }
                    placeholder="Optional legal name"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Admin Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={onboardForm.contact_email}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, contact_email: e.target.value })
                    }
                    placeholder="admin@company.com"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Initial Admin Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={onboardForm.password}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, password: e.target.value })
                    }
                    placeholder="Min 6 characters"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Admin First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={onboardForm.admin_first_name}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, admin_first_name: e.target.value })
                    }
                    placeholder="First Name"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={onboardForm.mobile_number}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, mobile_number: e.target.value })
                    }
                    placeholder="9876543210"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Office Address
                </label>
                <input
                  type="text"
                  value={onboardForm.address}
                  onChange={(e) =>
                    setOnboardForm({ ...onboardForm, address: e.target.value })
                  }
                  placeholder="City, State, Address"
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Subscription Tier
                  </label>
                  <select
                    value={onboardForm.plan_tier}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, plan_tier: e.target.value })
                    }
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="T700_STANDARD">T700 Standard (RFID / Finger)</option>
                    <option value="T800_ENTERPRISE">T800 Enterprise (Face / Palm / Finger)</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">
                    Staff Limit Quota
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={onboardForm.staff_limit}
                    onChange={(e) =>
                      setOnboardForm({ ...onboardForm, staff_limit: Number(e.target.value) })
                    }
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowOnboardModal(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={onboardSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {onboardSubmitting ? 'Onboarding...' : 'Complete Onboarding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Device Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100">
            <h3 className="text-lg font-bold text-slate-900 mb-1">
              Bind Hardware Machine
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Select an available T700 / T800 machine from inventory to bind with{' '}
              <span className="font-bold text-slate-800">{assignModal.org?.organization_name}</span>.
            </p>

            {unassignedDevices.length === 0 ? (
              <div className="bg-amber-50 text-amber-800 text-xs p-4 rounded-xl border border-amber-200 mb-4">
                No unassigned machines available in inventory. Connect a new machine to Port 4002 to register it.
              </div>
            ) : (
              <div className="space-y-4 mb-6">
                <label className="text-xs font-bold text-slate-700 block">
                  Select Machine Serial Number:
                </label>
                <select
                  value={assignModal.selectedDevId}
                  onChange={(e) =>
                    setAssignModal({ ...assignModal, selectedDevId: e.target.value })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {unassignedDevices.map((dev) => (
                    <option key={dev.dev_id} value={dev.dev_id}>
                      {dev.dev_id} ({dev.name || 'T700'} - IP: {dev.ip || 'DHCP'})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setAssignModal({ open: false, org: null, selectedDevId: '', saving: false })}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={assignModal.saving || unassignedDevices.length === 0}
                onClick={handleAssignDeviceSubmit}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-md transition disabled:opacity-50"
              >
                {assignModal.saving ? 'Binding...' : 'Confirm Binding'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {editModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Edit Client Organization
                </h3>
                <p className="text-xs text-slate-500">
                  Update biometric hardware plan, staff quota and company details.
                </p>
              </div>
              <button
                onClick={() => setEditModal({ open: false, org: null, form: {}, saving: false })}
                className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateClient} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  required
                  value={editModal.form.organization_name}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      form: { ...editModal.form, organization_name: e.target.value }
                    })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Legal / Registered Name
                </label>
                <input
                  type="text"
                  value={editModal.form.legal_name}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      form: { ...editModal.form, legal_name: e.target.value }
                    })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Plan Tier Selection */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Biometric Hardware Plan Tier
                </label>
                <select
                  value={editModal.form.plan_tier}
                  onChange={(e) => {
                    const newTier = e.target.value;
                    setEditModal({
                      ...editModal,
                      form: {
                        ...editModal.form,
                        plan_tier: newTier,
                        staff_limit: newTier === 'T800_ENTERPRISE' ? Math.max(200, editModal.form.staff_limit) : editModal.form.staff_limit
                      }
                    });
                  }}
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="T700_STANDARD">
                    T700 Standard (RFID / Fingerprint) - 50 Staff
                  </option>
                  <option value="T800_ENTERPRISE">
                    T800 Enterprise (Face / Palm / RFID) - 200+ Staff
                  </option>
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  {editModal.form.plan_tier === 'T800_ENTERPRISE'
                    ? '✨ Enterprise Tier: High-speed 3D Face Recognition + Palm Biometrics enabled.'
                    : '⚡ Standard Tier: Optical Fingerprint + RFID Smart Card enabled.'}
                </p>
              </div>

              {/* Staff Limit Quota */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Staff Limit Quota
                </label>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  value={editModal.form.staff_limit}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      form: { ...editModal.form, staff_limit: Number(e.target.value) }
                    })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Address */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={editModal.form.address}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      form: { ...editModal.form, address: e.target.value }
                    })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Tenant Status
                </label>
                <select
                  value={editModal.form.status}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      form: { ...editModal.form, status: e.target.value }
                    })
                  }
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditModal({ open: false, org: null, form: {}, saving: false })}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editModal.saving}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {editModal.saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
